import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Wallet, PieChart, Receipt, Settings, Target, Database } from "lucide-react";
import { Toaster } from "sonner";
import { fetchByCategory } from "../lib/api";

const navItems = [
  { to: "/spese", label: "Spese", icon: Receipt, testid: "nav-spese" },
  { to: "/saldo", label: "Saldo", icon: Wallet, testid: "nav-saldo" },
  { to: "/categorie", label: "Categorie", icon: PieChart, testid: "nav-categorie" },
  { to: "/budget", label: "Budget", icon: Target, testid: "nav-budget" },
  { to: "/dati", label: "Dati", icon: Database, testid: "nav-dati" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni" },
];

export default function Layout() {
  const [overCount, setOverCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    fetchByCategory({ year, month })
      .then((rows) => setOverCount((rows || []).filter((r) => r.over_budget).length))
      .catch(() => setOverCount(0));
  }, [location.pathname]);

  const renderBadge = (extra = "") => {
    if (!overCount) return null;
    return (
      <span
        data-testid="over-budget-badge"
        className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none ${extra}`}
        title={`${overCount} categori${overCount === 1 ? "a" : "e"} oltre budget questo mese`}
      >
        {overCount}
      </span>
    );
  };

  return (
    <div className="min-h-screen grain gradient-warmth">
      <header className="relative z-10 border-b border-stone-200/70 bg-white/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3" data-testid="app-brand">
            <div className="w-10 h-10 rounded-2xl bg-[#377D22] text-white grid place-items-center font-display font-bold text-lg">M&D</div>
            <div>
              <div className="font-display font-semibold text-lg leading-none">Spese di casa</div>
              <div className="text-xs text-stone-500 tracking-widest uppercase mt-1">Madda &amp; Doio</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `pill-btn px-4 py-2 text-sm font-medium flex items-center gap-2 ${
                    isActive ? "bg-[#377D22] text-white" : "text-stone-600 hover:bg-stone-100"
                  }`
                }
              >
                <n.icon size={16} strokeWidth={1.5} /> {n.label}
                {n.to === "/budget" && renderBadge()}
              </NavLink>
            ))}
          </nav>
          <div className="md:hidden flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`${n.testid}-mobile`}
                className={({ isActive }) =>
                  `pill-btn p-2 relative ${isActive ? "bg-[#377D22] text-white" : "text-stone-600 hover:bg-stone-100"}`
                }
              >
                <n.icon size={18} strokeWidth={1.5} />
                {n.to === "/budget" && overCount > 0 && (
                  <span
                    data-testid="over-budget-badge-mobile"
                    className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
                  >
                    {overCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-10">
        <Outlet />
      </main>
      <footer className="relative z-10 max-w-7xl mx-auto px-6 py-10 text-xs text-stone-400 text-center tracking-widest uppercase">
        Spese condivise di casa · Madda &amp; Doio
      </footer>
      <Toaster position="top-right" richColors />
    </div>
  );
}
